/**
 * Author: Andrey Morozov
 * Confidential. Proprietary code. Do not distribute without authorization.
 *
 * env.ts — env (services/operator-web-app).
 */

/**
 * Runtime config from environment.
 * Vite exposes only VITE_* to the client; validate at app load.
 */

const raw = {
  wsOperatorUrl: import.meta.env.VITE_WS_OPERATOR_URL ?? '',
  apiBaseUrl: import.meta.env.VITE_API_BASE_URL ?? '',
  appEnv: (import.meta.env.VITE_APP_ENV ?? 'development') as
    | 'development'
    | 'staging'
    | 'production',
  basePath: import.meta.env.BASE_URL ?? '/',
};

function getWsOperatorUrl(): string {
  if (raw.wsOperatorUrl) return raw.wsOperatorUrl;
  const base =
    typeof window !== 'undefined' ? window.location : { host: 'localhost', protocol: 'ws:' };
  const protocol = base.protocol === 'https:' ? 'wss:' : 'ws:';
  const host = typeof base === 'object' && 'host' in base ? base.host : 'localhost:8080';
  return `${protocol}//${host}/ws/operator`;
}

function getWsCallUrl(): string {
  const base = raw.apiBaseUrl || (typeof window !== 'undefined' ? window.location.origin : '');
  const u = base.replace(/^http/, 'ws');
  return `${u.replace(/\/$/, '')}/ws/call`;
}

function getSipWsUrl(): string {
  const env = import.meta.env.VITE_SIP_WS_URL as string | undefined;
  if (env?.trim()) return env.trim();
  const base =
    typeof window !== 'undefined' ? window.location : { hostname: 'localhost', protocol: 'ws:' };
  const protocol = base.protocol === 'https:' ? 'wss:' : 'ws:';
  const host =
    typeof base === 'object' && 'host' in base
      ? (base as { host: string }).host
      : `${base.hostname}:9780`;
  return `${protocol}//${host}`;
}

function getSipDomain(): string {
  const env = import.meta.env.VITE_SIP_DOMAIN as string | undefined;
  if (env?.trim()) return env.trim();
  return 'ai-agent.local';
}

export const appConfig = {
  wsOperatorUrl: getWsOperatorUrl(),
  wsCallUrl: (import.meta.env.VITE_WS_CALL_URL as string) || getWsCallUrl(),
  /** E3-S2 T1: SIP over WebSocket URL for WebRTC (Kamailio). */
  sipWsUrl: getSipWsUrl(),
  /** E3-S2 T1: SIP domain for WebRTC. */
  sipDomain: getSipDomain(),
  apiBaseUrl: raw.apiBaseUrl || (typeof window !== 'undefined' ? window.location.origin : ''),
  appEnv: raw.appEnv,
  basePath: raw.basePath,
  isDev: raw.appEnv === 'development',
  /** Operator ID for presence (from env or default). */
  operatorId: (import.meta.env.VITE_OPERATOR_ID as string) || 'demo-operator',
  /** Client version sent in presence_register. */
  clientVersion: (import.meta.env.VITE_APP_VERSION as string) || '0.1.0',
} as const;

const DEVICE_ID_KEY = 'operator-web-app-device-id';
const OPERATOR_ID_KEY = 'operator-web-app-operator-id';

/**
 * Operator ID for presence/voice. Runtime value from sessionStorage overrides env default.
 * Plan 2.1: minimal login — user can set operator ID on Login page.
 */
export function getOperatorId(): string {
  if (typeof window === 'undefined') return appConfig.operatorId;
  try {
    const stored = sessionStorage.getItem(OPERATOR_ID_KEY);
    if (stored?.trim()) return stored.trim();
  } catch {
    /* ignore */
  }
  return appConfig.operatorId;
}

/** Set operator ID (e.g. after "login"); used by Login page. */
export function setOperatorId(operatorId: string): void {
  if (typeof window === 'undefined') return;
  try {
    if (operatorId.trim()) {
      sessionStorage.setItem(OPERATOR_ID_KEY, operatorId.trim());
    } else {
      sessionStorage.removeItem(OPERATOR_ID_KEY);
    }
  } catch {
    /* ignore */
  }
}

/** Stable device id for this browser tab/session (persisted in sessionStorage). */
export function getOrCreateDeviceId(): string {
  if (typeof window === 'undefined') return 'server';
  try {
    let id = sessionStorage.getItem(DEVICE_ID_KEY);
    if (!id) {
      id = crypto.randomUUID?.() ?? `dev-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
      sessionStorage.setItem(DEVICE_ID_KEY, id);
    }
    return id;
  } catch {
    return `dev-${Date.now()}`;
  }
}

export function validateConfig(): { ok: true } | { ok: false; errors: string[] } {
  const errors: string[] = [];
  if (
    !appConfig.wsOperatorUrl.startsWith('ws://') &&
    !appConfig.wsOperatorUrl.startsWith('wss://')
  ) {
    errors.push('VITE_WS_OPERATOR_URL must be a WebSocket URL (ws:// or wss://)');
  }
  return errors.length ? { ok: false, errors } : { ok: true };
}
